import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Mail, Building, Send, Clock, CheckCircle, Award, Globe, Users, Instagram, Linkedin, Twitter, Facebook, Youtube } from 'lucide-react';

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    countryCode: "+91",
    contactNumber: "",
    email: "",
    altPhone: "",
    organisation: "",
    query: "",
  });

  const [status, setStatus] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch("https://script.google.com/macros/s/AKfycbwXRRHvu61QeZLuu21XmGoFRf9FjBD5Fu6vyu3_iEp9zcqKytq8tNgKr-wmwlDUMfLq/exec", {
        method: "POST",
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        setStatus("success");
        setFormData({
          name: "", 
          countryCode: "+91",
          contactNumber: "", 
          email: "",
          altPhone: "", 
          organisation: "", 
          query: "",
        });
        
        // Reset status after 5 seconds
        setTimeout(() => {
          setStatus("");
        }, 5000);
      } else {
        setStatus("error");
      }
    } catch (error) {
      setStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const services = [
    'Studies & Planning',
    'Surveys & Investigations', 
    'Design & Construction Engineering',
    'Project Management',
    'Energy & Environment',
    'Transaction Advisory',
    'Water Resources & Agricultural Development',
    'Environmental & Public Health Engineering',
    'Railways',
    'Roads & Highways',
    'Bridges & Flyovers',
    'Metro',
    'Power Plants & Telecommunications',
    'Offshore & Coastal Engineering',
    'Urban & Industrial Development',
    'Heritage Building Rehabilitation'
  ];



  const socialLinks = [
    { icon: Instagram, name: "Instagram", url: "https://instagram.com" },
    { icon: Linkedin, name: "LinkedIn", url: "https://linkedin.com" },
    { icon: Twitter, name: "Twitter", url: "https://twitter.com" },
    { icon: Facebook, name: "Facebook", url: "https://facebook.com" },
    { icon: Youtube, name: "YouTube", url: "https://youtube.com" },
  ];

  const burgundy = '#87212e';
  const burgundyLight = '#a0002a';
  const burgundyDark = '#6d1a25';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="relative min-h-screen overflow-hidden">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat transform scale-105"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')`
          }}
        />
        
        {/* Gradient Overlays */}
        <div className="absolute inset-0" style={{
          background: `linear-gradient(135deg, ${burgundyDark}f0 0%, ${burgundy}e6 50%, ${burgundyLight}cc 100%)`
        }}></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/30"></div>
        
        {/* Animated Geometric Elements */}
        <div className="absolute top-20 left-10 w-32 h-32 border-2 border-white/20 rotate-45 animate-pulse"></div>
        <div className="absolute bottom-40 right-20 w-24 h-24 border border-white/30 rounded-full animate-bounce"></div>
        <div className="absolute top-1/3 right-1/4 w-16 h-16 bg-white/10 rotate-12 animate-pulse"></div>

        {/* Hero Content */}
        <div className="relative min-h-screen flex items-center justify-center text-center px-4 py-12 sm:py-16">
          <div className={`max-w-5xl mx-auto transform transition-all duration-1000 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
          }`}>
            {/* Logo */}
            

            {/* Tagline */}
            <div className="mb-8 sm:mb-12">
              <p className="text-lg sm:text-2xl md:text-3xl text-white/95 tracking-wider mb-4 sm:mb-6 font-light">
                CONNECT <span className="text-white/70">|</span> COLLABORATE <span className="text-white/70">|</span> CREATE IMPACT
              </p>
              <div className="w-32 h-1 mx-auto mb-8" style={{background: `linear-gradient(90deg, transparent, white, transparent)`}}></div>
            </div>

            {/* Main Heading */}
            <div className="mb-8 sm:mb-12">
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-6 leading-tight">
                Let's Build
                <span className="block bg-gradient-to-r from-white to-white/80 bg-clip-text text-transparent">
                  A Better Tomorrow Together
                </span>
              </h2>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/80 max-w-3xl mx-auto leading-relaxed font-light">
                Empowering youth, fostering global cooperation, and driving sustainable innovation. Reach out to collaborate on meaningful change.
              </p>
            </div>

            {/* CTA Button */}
           

            {/* Stats */}
            
          </div>
        </div>

        {/* Scroll Indicator */}
       
      </div>

      {/* Business Inquiry Section */}
      <div className="relative z-20 -mt-32 md:-mt-40 lg:-mt-48 w-full">
        <div className="px-4 md:px-8 lg:px-16 xl:px-24 2xl:px-32 w-full">
          <div className="bg-white rounded-[40px] shadow-2xl p-8 md:p-12 w-full">
            <h2 className="text-4xl md:text-5xl font-bold text-center mb-12" style={{color: burgundy}}>
              Get in Touch
            </h2>
            
            <div id="contact-form" className="max-w-4xl mx-auto">
              {status === "success" ? (
                <div className="text-center py-12 sm:py-16">
                  <div className="w-16 h-16 sm:w-20 sm:h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </div>
                  <h4 className="text-xl sm:text-2xl font-bold text-green-600 mb-3 sm:mb-4">Message Sent Successfully!</h4>
                  <p className="text-gray-600 text-base sm:text-lg">Thank you for reaching out. I'll get back to you soon!</p>
                </div>
              ) : (
                <div className="relative overflow-hidden">
                  {/* Background Pattern */}
                  <div className="absolute top-0 right-0 w-64 h-64 opacity-5" style={{
                    background: `radial-gradient(circle, ${burgundy} 1px, transparent 1px)`,
                    backgroundSize: '20px 20px'
                  }}></div>
                  
                  <div className="relative z-10">
                    <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <div className="group">
                          <label className="block text-sm font-semibold text-gray-700 mb-3">
                            Name *
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="w-full px-6 py-4 border-2 border-gray-200 rounded-xl focus:outline-none transition-all duration-300"
                            onFocus={(e) => {
                              e.target.style.borderColor = burgundy;
                              e.target.style.boxShadow = `0 0 0 3px ${burgundy}20`;
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#e5e7eb';
                              e.target.style.boxShadow = 'none';
                            }}
                            placeholder="Enter your full name"
                          />
                        </div>
                        
                        <div className="group">
                          <label className="block text-sm font-semibold text-gray-700 mb-3">
                            Contact Number *
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="text"
                              name="countryCode"
                              value={formData.countryCode}
                              onChange={handleChange}
                              placeholder="+91"
                              className="w-20 px-4 py-4 border-2 border-gray-200 rounded-xl focus:outline-none transition-all duration-300"
                              onFocus={(e) => {
                                e.target.style.borderColor = burgundy;
                                e.target.style.boxShadow = `0 0 0 3px ${burgundy}20`;
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = '#e5e7eb';
                                e.target.style.boxShadow = 'none';
                              }}
                            />
                            <input
                              type="text"
                              name="contactNumber"
                              value={formData.contactNumber}
                              onChange={handleChange}
                              required
                              className="flex-1 px-6 py-4 border-2 border-gray-200 rounded-xl focus:outline-none transition-all duration-300"
                              onFocus={(e) => {
                                e.target.style.borderColor = burgundy;
                                e.target.style.boxShadow = `0 0 0 3px ${burgundy}20`;
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = '#e5e7eb';
                                e.target.style.boxShadow = 'none';
                              }}
                              placeholder="Enter your contact number"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-3">
                            Email Address *
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-6 py-4 border-2 border-gray-200 rounded-xl focus:outline-none transition-all duration-300"
                            onFocus={(e) => {
                              e.target.style.borderColor = burgundy;
                              e.target.style.boxShadow = `0 0 0 3px ${burgundy}20`;
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#e5e7eb';
                              e.target.style.boxShadow = 'none';
                            }}
                            placeholder="Enter your email address"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-3">
                            Alternative Phone
                          </label>
                          <input
                            type="tel"
                            name="altPhone"
                            value={formData.altPhone}
                            onChange={handleChange}
                            className="w-full px-6 py-4 border-2 border-gray-200 rounded-xl focus:outline-none transition-all duration-300"
                            onFocus={(e) => {
                              e.target.style.borderColor = burgundy;
                              e.target.style.boxShadow = `0 0 0 3px ${burgundy}20`;
                            }}
                            onBlur={(e) => {
                              e.target.style.borderColor = '#e5e7eb';
                              e.target.style.boxShadow = 'none';
                            }}
                            placeholder="Enter alternative phone number"
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          Organisation
                        </label>
                        <input
                          type="text"
                          name="organisation"
                          value={formData.organisation}
                          onChange={handleChange}
                          className="w-full px-6 py-4 border-2 border-gray-200 rounded-xl focus:outline-none transition-all duration-300"
                          onFocus={(e) => {
                            e.target.style.borderColor = burgundy;
                            e.target.style.boxShadow = `0 0 0 3px ${burgundy}20`;
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e5e7eb';
                            e.target.style.boxShadow = 'none';
                          }}
                          placeholder="Enter your organisation name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-3">
                          Describe your Query *
                        </label>
                        <textarea
                          name="query"
                          value={formData.query}
                          onChange={handleChange}
                          required
                          rows={6}
                          className="w-full px-6 py-4 border-2 border-gray-200 rounded-xl focus:outline-none transition-all duration-300 resize-vertical"
                          onFocus={(e) => {
                            e.target.style.borderColor = burgundy;
                            e.target.style.boxShadow = `0 0 0 3px ${burgundy}20`;
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor = '#e5e7eb';
                            e.target.style.boxShadow = 'none';
                          }}
                          placeholder="Share your thoughts, ideas, or collaboration opportunities..."
                        ></textarea>
                      </div>

                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="group w-full text-white py-6 px-8 rounded-xl font-bold text-lg transform hover:scale-105 transition-all duration-300 shadow-xl relative overflow-hidden disabled:opacity-70 disabled:cursor-not-allowed"
                        style={{
                          background: `linear-gradient(135deg, ${burgundy} 0%, ${burgundyLight} 100%)`,
                        }}
                        onMouseEnter={(e) => {
                          if (!isSubmitting) {
                            e.target.style.background = `linear-gradient(135deg, ${burgundyLight} 0%, ${burgundyDark} 100%)`;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSubmitting) {
                            e.target.style.background = `linear-gradient(135deg, ${burgundy} 0%, ${burgundyLight} 100%)`;
                          }
                        }}
                      >
                        <span className="relative z-10 flex items-center justify-center">
                          {isSubmitting ? (
                            <>
                              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                              Sending...
                            </>
                          ) : (
                            <>
                              Submit
                              <Send className="ml-3 w-5 h-5 transform group-hover:translate-x-1 transition-transform" />
                            </>
                          )}
                        </span>
                        <div className="absolute inset-0 bg-white/10 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
                      </button>

                      {status === "error" && (
                        <div className="text-center p-4 bg-red-50 border border-red-200 rounded-xl">
                          <p className="text-red-600">Failed to send message. Please try again later.</p>
                        </div>
                      )}
                    </form>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Contact Information & Social Links */}
      {/* <div className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
\          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{color: burgundy}}>
              Offices and Address
            </h2>
            <div className="w-24 h-1 mx-auto" style={{background: `linear-gradient(90deg, transparent, ${burgundy}, transparent)`}}></div>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <div className="bg-white rounded-3xl shadow-2xl p-8 border-l-4" style={{borderLeftColor: burgundy}}>
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4" style={{backgroundColor: `${burgundy}15`}}>
                    <Building style={{color: burgundy}} size={24} />
                  </div>
                  <h3 className="text-2xl font-bold" style={{color: burgundy}}>
                    Corporate Office
                  </h3>
                </div>
                <div className="space-y-3 text-gray-700">
                  <div className="flex items-start">
                    <MapPin className="mr-3 mt-1 flex-shrink-0" style={{color: burgundy}} size={18} />
                    <span className="leading-relaxed">505, Synergy Tower, Near Vodafone House, Corporate Road, Prahladnagar, Ahmedabad - 380015, Gujarat, India</span>
                  </div>
                  <div className="flex items-center">
                    <Phone className="mr-3" style={{color: burgundy}} size={18} />
                    <span>+91 079 4600 1587</span>
                  </div>
                  <div className="flex items-center">
                    <Mail className="mr-3" style={{color: burgundy}} size={18} />
                    <span>info@rayinfra.in</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-3xl shadow-2xl p-8 border-l-4" style={{borderLeftColor: burgundy}}>
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mr-4" style={{backgroundColor: `${burgundy}15`}}>
                    <Building style={{color: burgundy}} size={24} />
                  </div>
                  <h3 className="text-2xl font-bold" style={{color: burgundy}}>
                    Registered Office
                  </h3>
                </div>
                <div className="space-y-3 text-gray-700">
                  <div className="flex items-start">
                    <MapPin className="mr-3 mt-1 flex-shrink-0" style={{color: burgundy}} size={18} />
                    <span className="leading-relaxed">403, Gunjan Tower, Nr. Inorbit Mall, Alembic-Gorwa Rd, Subhanpura, Vadodara, Gujarat 390023</span>
                  </div>
                  <div className="flex items-center">
                    <Phone className="mr-3" style={{color: burgundy}} size={18} />
                    <span>+91 265 4003140</span>
                  </div>
                  <div className="flex items-center">
                    <Mail className="mr-3" style={{color: burgundy}} size={18} />
                    <span>info@rayinfra.in</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-8">
              <div className="bg-white rounded-3xl shadow-2xl p-8">
                <h3 className="text-2xl font-bold mb-6 text-center" style={{color: burgundy}}>
                  @RayInfra
                </h3>
                <p className="text-gray-600 text-center mb-6">Follow us on your favorite channels.</p>
                <div className="space-y-4">
                  {socialLinks.map((platform) => (
                    <a
                      key={platform.name}
                      href={platform.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center p-3 rounded-xl transition-all duration-300 hover:bg-gray-50 group"
                      style={{'--hover-color': burgundy}}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = `${burgundy}10`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <platform.icon className="w-6 h-6 mr-4 transition-colors" style={{color: burgundy}} />
                      <span className="text-gray-700 group-hover:font-semibold transition-all">{platform.name}</span>
                    </a>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-3xl shadow-2xl p-8">
                <h3 className="text-2xl font-bold mb-6 text-center" style={{color: burgundy}}>
                  Email
                </h3>
                <p className="text-gray-700 leading-relaxed text-center">
                  If you have any inquiry or need more information please contact us at{' '}
                  <strong style={{color: burgundy}}>info@rayinfra.in</strong>.
                </p>
              </div>

              
            </div>
          </div>
        </div>
      </div> */}
    </div>
  );
};

export default ContactUs;